import { useState, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, Plus, Minus, Package, AlertTriangle, Lock } from 'lucide-react';
import { apiGet } from '../lib/api';

/**
 * MaterialItemSelector — shared picker for Material Requisition, Issue, Return
 * and Stocktake. Universe = the project's MD-approved Project Material Sheet
 * ∪ FIFO stock layers (BOQ ∪ layers when no PMS). Availability always comes
 * from stock_layers via GET /stores/material-items.
 *
 * selectedItems: [{ boq_id, quantity, description, unit, material_id, pms_item_id }]
 * boq_id is the stock line key: a real BOQ id or "mat:<material_id>".
 */
export function MaterialItemSelector({ projectId, officeLocationId, mode = 'issue', selectedItems = [], onItemsChange, disabled = false, sourceType, sourceProjectId }) {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ source: null, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const inStockOnly = mode === 'issue' || mode === 'return' || mode === 'transfer';

  const fetchItems = useCallback(async () => {
    if (!projectId && !officeLocationId) return;
    setLoading(true);
    try {
      const params = { mode, search: searchTerm || undefined, in_stock_only: inStockOnly };
      if (projectId) params.project_id = projectId; else params.office_location_id = officeLocationId;
      // Fix #3: pass source type for godown/other-project availability
      if (mode === 'mr' && sourceType && sourceType !== 'own_site') {
        params.source_type = sourceType;
        if (sourceProjectId) params.source_project_id = sourceProjectId;
      }
      const data = await apiGet('/stores/material-items', params);
      setItems(data.items || []);
      setMeta({ source: data.source, total: data.total });
    } catch (err) {
      console.error('Error fetching material items:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, officeLocationId, mode, searchTerm, inStockOnly, sourceType, sourceProjectId]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const capFor = (it) => {
    if (mode === 'stocktake') return null;
    if (mode === 'return') return it.returnable_qty ?? 0;          // issued − already returned
    if (mode === 'mr') return it.remaining_cap ?? null;           // PMS cap (planned − issued)
    if (mode === 'transfer') return it.available_qty;              // stock only — PMS budget is irrelevant to a transfer-out
    if (it.remaining_cap == null) return it.available_qty;
    return Math.min(it.available_qty, it.remaining_cap);           // issue: stock ∧ PMS cap
  };
  const selected = (key) => selectedItems.find(s => s.boq_id === key);
  const setQty = (it, qty) => {
    const cap = capFor(it);
    const q = Math.max(0, cap == null ? qty : Math.min(qty, cap));
    const rest = selectedItems.filter(s => s.boq_id !== it.boq_id);
    onItemsChange(q > 0
      ? [...rest, { boq_id: it.boq_id, quantity: q, description: it.description, unit: it.unit, material_id: it.material_id, pms_item_id: it.pms_item_id }]
      : rest);
  };

  if (!projectId && !officeLocationId) return <p className="text-sm text-slate-400">Pick a project first.</p>;

  return (
    <div className="space-y-2" data-testid="material-item-selector">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search item code / description…"
            className="pl-8 h-9" disabled={disabled} data-testid="material-item-search" />
        </div>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 whitespace-nowrap" data-testid="material-item-source">
          {mode === 'transfer' ? 'Stock on hand' : (() => {
            const base = meta.source === 'pms' ? 'Project Material Sheet' : meta.source === 'boq_fallback' ? 'BOQ + Stock (no PMS)' : '';
            const hasMaster = items.some(i => i.source === 'master');
            return hasMaster ? `${base}${base ? ' + ' : ''}Master (off-sheet)` : base;
          })()}
        </span>
      </div>
      <div className="border rounded-md max-h-72 overflow-y-auto divide-y">
        {loading && <div className="p-3 text-xs text-slate-400">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-4 text-sm text-slate-500 flex items-center gap-2"><Package className="w-4 h-4" />
            {mode === 'return' ? "Nothing issued to this project yet — nothing to return."
              : mode === 'mr' ? 'No materials found — try a different search term.'
              : inStockOnly ? 'No stock on hand for this project.' : 'No material rows for this project.'}
          </div>
        )}
        {items.map(it => {
          const sel = selected(it.boq_id);
          const cap = capFor(it);
          const blocked = cap != null && cap <= 0;
          return (
            <div key={it.boq_id} className={`flex items-center gap-3 px-3 py-2 text-sm ${sel ? 'bg-indigo-50/60' : ''}`} data-testid={`material-row-${it.boq_id}`}>
              <div className="flex-1 min-w-0">
                {it.item_code && <div className="font-mono text-[10px] text-slate-400">{it.item_code}</div>}
                <div className="font-medium text-slate-800 leading-snug line-clamp-2" title={it.description || ''}>
                  {it.description || '—'}
                  {it.not_on_sheet && <span className="ml-2 text-[10px] font-display font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5" data-testid={`material-not-on-sheet-${it.boq_id}`}>Not on sheet</span>}
                </div>
                <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                  <span>Unit {it.unit || '—'}</span>
                  {it.planned_qty > 0 && <span>Planned {it.planned_qty}</span>}
                  {mode === 'return' ? (
                    <>
                      <span>Issued {it.issued_qty}</span>
                      <span>Returned {it.returned_qty}</span>
                      <span className={it.returnable_qty > 0 ? 'text-emerald-700 font-semibold' : 'text-slate-400'}>Returnable {it.returnable_qty}</span>
                    </>
                  ) : (
                    <>
                      <span>Received {it.received_qty}</span>
                      <span>Issued {it.issued_qty}</span>
                      <span className={it.available_qty > 0 ? 'text-emerald-700 font-semibold' : 'text-slate-400'}>Available {it.available_qty}</span>
                      {/* Fix #3: show godown/other-project availability when source_type is set */}
                      {mode === 'mr' && sourceType && sourceType !== 'own_site' && it.source_available_qty != null && (
                        <span className={it.source_available_qty > 0 ? 'text-sky-700 font-semibold' : 'text-slate-400'} data-testid={`source-avail-${it.boq_id}`}>
                          {sourceType === 'godown' ? 'at Godown' : 'at source'} {it.source_available_qty}
                        </span>
                      )}
                    </>
                  )}
                  {it.remaining_cap != null && <span className="text-amber-700">Cap left {it.remaining_cap}</span>}
                  {it.in_transit_qty > 0 && <span className="text-sky-700" data-testid={`material-in-transit-${it.boq_id}`}>In transit {it.in_transit_qty}</span>}
                  {it.client_supplied && <span className="text-sky-700">Client-supplied</span>}
                </div>
              </div>
              {blocked ? (
                <span className="text-[11px] text-rose-600 flex items-center gap-1" title={mode === 'transfer' ? 'No stock on hand' : mode === 'return' ? 'Nothing left to return' : 'PMS cap reached / no stock'}><Lock className="w-3 h-3" /> Capped</span>
              ) : (
                <div className="flex items-center gap-1">
                  <Button type="button" size="icon" variant="outline" className="h-7 w-7" disabled={disabled || !sel}
                    onClick={() => setQty(it, (sel?.quantity || 0) - 1)}><Minus className="w-3 h-3" /></Button>
                  <Input type="number" min="0" step="any" value={sel?.quantity ?? ''} placeholder="0" disabled={disabled}
                    onChange={e => setQty(it, parseFloat(e.target.value) || 0)} className="h-7 w-20 text-right"
                    data-testid={`material-qty-${it.boq_id}`} />
                  <Button type="button" size="icon" variant="outline" className="h-7 w-7" disabled={disabled}
                    onClick={() => setQty(it, (sel?.quantity || 0) + 1)}><Plus className="w-3 h-3" /></Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selectedItems.length > 0 && (
        <div className="text-xs text-slate-600 flex items-center gap-1" data-testid="material-selected-count">
          <AlertTriangle className="w-3 h-3 text-amber-500" /> {selectedItems.length} line(s) selected — quantities are capped at {mode === 'transfer' ? 'stock on hand.' : 'stock on hand and the PMS cap.'}
        </div>
      )}
    </div>
  );
}

export default MaterialItemSelector;
